/* eslint-disable max-lines */
import { Situation, SituationColor } from '@hakimo-ui/hakimo/types';
import { Button, Tooltip } from '@hakimo-ui/shared/ui-base';
import {
  InformationCircleIcon,
  PencilIcon,
  PlusIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';
import { useState } from 'react';
import { normalizeSchedules } from '../../sop-workflow/scheduleUtils';
import CameraLabels from '../camera-labels/CameraLabels';
import ScheduleLabels from '../schedule-labels/ScheduleLabels';
import CreateUpdateSituationModal from '../situation/CreateUpdateSituationModal';
import {
  getInfoTextByFieldId,
  renderEmptyState,
  renderSubItems,
} from '../utils';
import SOPCard from './SOPCard';
import { getSituationColorClasses } from './utils';

interface Props {
  situations: Situation[];
  onChange: (val: Situation[]) => void;
  isEditing: boolean;
  locationId?: string;
}

export function SOPSituationsCard(props: Props) {
  const { situations, onChange, isEditing, locationId } = props;

  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [updateItem, setUpdateItem] = useState<Situation>();
  const [isNewItem, setIsNewItem] = useState(false);
  const [expandedItems, setExpandedItems] = useState<Record<number, boolean>>(
    {}
  );

  const handleAddItem = () => {
    setIsNewItem(true);
    setUpdateItem({
      label: '',
      color: SituationColor.RED,
      subSituations: [],
      cameras: [],
    });
    setShowUpdateModal(true);
  };

  const handleEditItem = (i: number) => () => {
    setShowUpdateModal(true);
    setUpdateItem(situations[i]);
  };

  const handleDeleteItem = (i: number) => () => {
    const updatedItems = [...situations];
    updatedItems.splice(i, 1);
    onChange(updatedItems);
  };

  const handleCloseModal = () => {
    setIsNewItem(false);
    setShowUpdateModal(false);
    setUpdateItem(undefined);
  };

  const handleUpdateFromModal = (val: Situation) => {
    if (!updateItem) {
      return;
    }
    const updatedItems = [...situations];
    if (isNewItem) {
      updatedItems.push(val);
    } else {
      updatedItems[situations.indexOf(updateItem)] = val;
    }
    onChange(updatedItems);
    handleCloseModal();
  };

  const toggleExpanded = (index: number) => {
    setExpandedItems((prev) => ({
      ...prev,
      [index]: !prev[index],
    }));
  };

  const headerAction = isEditing ? (
    <Button
      variant="primary"
      onClick={handleAddItem}
      className="flex items-center gap-2"
    >
      <PlusIcon className="h-4 w-4" />
      Add
    </Button>
  ) : null;

  const headerInfo = (
    <Tooltip
      text={getInfoTextByFieldId('situations') || ''}
      size="large"
      colorModifier="info"
    >
      <InformationCircleIcon className="h-5 w-5" />
    </Tooltip>
  );

  return (
    <>
      <SOPCard
        title="Situations"
        headerAction={headerAction}
        headerInfo={headerInfo}
      >
        {isEditing ? (
          <div className="flex flex-wrap gap-4">
            {situations.map((item, i) => (
              <div
                className={`${getSituationColorClasses(item.color)} max-w-sm`}
                key={i}
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="font-medium">{item.label}</span>
                  <div className="flex items-center">
                    <Button variant="icon" onClick={handleEditItem(i)}>
                      <PencilIcon className="h-5 w-5" />
                    </Button>
                    <Button variant="icon" onClick={handleDeleteItem(i)}>
                      <TrashIcon className="h-5 w-5" />
                    </Button>
                  </div>
                </div>
                {item.subSituations &&
                  item.subSituations.length > 0 &&
                  renderSubItems(item.subSituations, expandedItems[i], () =>
                    toggleExpanded(i)
                  )}
                <ScheduleLabels
                  schedules={normalizeSchedules(item)}
                  className="mt-1"
                />
                {item.cameras && item.cameras.length > 0 && (
                  <div className="mt-2">
                    <CameraLabels cameras={item.cameras} />
                  </div>
                )}
              </div>
            ))}
          </div>
        ) : situations && situations.length > 0 ? (
          <div className="flex flex-wrap gap-4">
            {situations.map((item, i) => (
              <div className={getSituationColorClasses(item.color)} key={i}>
                <div className="flex items-center">
                  <span className="font-medium">{item.label}</span>
                </div>
                {item.subSituations &&
                  item.subSituations.length > 0 &&
                  renderSubItems(item.subSituations, expandedItems[i], () =>
                    toggleExpanded(i)
                  )}
                <ScheduleLabels
                  schedules={normalizeSchedules(item)}
                  className="mt-1"
                />
                {item.cameras && item.cameras.length > 0 && (
                  <div className="mt-2">
                    <CameraLabels cameras={item.cameras} />
                  </div>
                )}
              </div>
            ))}
          </div>
        ) : (
          renderEmptyState()
        )}
      </SOPCard>
      {showUpdateModal && updateItem && (
        <CreateUpdateSituationModal
          onClose={handleCloseModal}
          situation={updateItem}
          onSubmit={handleUpdateFromModal}
          isNew={isNewItem}
          locationId={locationId}
        />
      )}
    </>
  );
}

export default SOPSituationsCard;
